import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";

// ── Project Requirements Document ────────────────────────────────────────────
//
// Every action returns what the caller needs to decide what to show next
// instead of only pushing state, because several of these are two-step: the
// server answers 409 with "are you sure, and here is what it will cost" and the
// UI has to put that question to the person before calling again with confirm.

export type PrdStatus = "Draft" | "In Review" | "Approved" | "Changes Required" | "Outdated";

export interface PrdSection {
  key: string;
  title: string;
  intent?: string;
  type?: string;
  required?: boolean;
  commercial?: boolean;
  order?: number;
}

export interface PrdRequirement {
  ref: string;
  text: string;
  kind: "functional" | "non-functional";
  priority: string;
  source: string;
  sourceDetail: string;
  milestone: string;
  acceptance: string;
}

export interface PrdReview {
  missing: string[];
  conflicts: { statement: string; sources: string[] }[];
  assumptions: string[];
  recommendations: string[];
  unreadableSources: { name: string; reason: string }[];
  generatedBy?: string;
  generationError?: string;
  generatedAt?: string;
}

export interface PrdPermissions {
  view: boolean;
  generate: boolean;
  edit: boolean;
  regenerate: boolean;
  submit: boolean;
  approve: boolean;
  requestChanges: boolean;
  download: boolean;
  unlink: boolean;
}

export interface Prd {
  _id: string;
  project: string;
  name: string;
  status: PrdStatus;
  templateName: string;
  templateCategory: string;
  templateSections: PrdSection[];
  contentHtml: string;
  requirements: PrdRequirement[];
  aiReview: PrdReview;
  editedSections: string[];
  outdatedReasons: string[];
  outdatedAt: string | null;
  visibleToClient: boolean;
  artifact: {
    fileName: string;
    mimeType: string;
    size: number;
    checksum: string;
    publishedAt: string;
    storageProvider: string;
    url: string;
  } | null;
  erp: {
    status: string;
    erpProjectId: string;
    erpDocumentId: string;
    syncedAt: string | null;
    lastError: string;
    retryCount: number;
  };
  createdByName: string;
  lastEditedByName: string;
  lastEditedAt: string | null;
  submittedByName: string;
  submittedAt: string | null;
  approvedByName: string;
  approvedAt: string | null;
  changesRequestedNote: string;
  audit: any[];
  createdAt: string;
  updatedAt: string;
}

export interface PrdValidation {
  ok: boolean;
  missingSections: string[];
  emptySections: string[];
  addedSections: string[];
  structureChanged: boolean;
}

// What the server says when it cannot pick a template. Carried through to the
// UI verbatim — it already contains the sentence a person needs to read.
export interface TemplateState {
  ok: boolean;
  code?: "CATEGORY_MISSING" | "CATEGORY_AMBIGUOUS" | "CATEGORY_UNMAPPED" | "NO_TEMPLATE";
  message?: string;
  category?: string;
  candidates?: string[];
  template?: { name: string } | null;
}

interface PrdState {
  prd: Prd | null;
  permissions: PrdPermissions | null;
  template: TemplateState | null;
  categories: string[];
  validation: PrdValidation | null;
  conflicts: { key: string; title: string; regenerated: string }[];
  loading: boolean;
  busy: string;

  reset: () => void;
  fetch: (projectId: string) => Promise<void>;
  generate: (projectId: string, opts?: { confirmedCategory?: string; includeCommercial?: boolean }) => Promise<TemplateState | null>;
  save: (prdId: string, contentHtml: string, opts?: { confirmUnapprove?: boolean; visibleToClient?: boolean }) => Promise<{ needsConfirm?: string } | null>;
  regenerate: (prdId: string, opts?: { confirm?: boolean; confirmedCategory?: string; includeCommercial?: boolean }) => Promise<{ needsConfirm?: string; editedSections?: string[] } | null>;
  submit: (prdId: string, force?: boolean) => Promise<boolean>;
  approve: (prdId: string, opts?: { confirm?: boolean; force?: boolean }) => Promise<{ needsConfirm?: string; review?: any; incomplete?: PrdValidation } | null>;
  requestChanges: (prdId: string, note: string) => Promise<boolean>;
  retryErp: (prdId: string) => Promise<void>;
  republish: (prdId: string) => Promise<void>;
  download: (prdId: string, fileName: string) => Promise<void>;
  unlink: (prdId: string) => Promise<boolean>;
}

const apply = (set: any, data: any) =>
  set({
    prd: data.prd ?? null,
    permissions: data.permissions ?? null,
    template: data.template ?? null,
    categories: data.categories ?? [],
    validation: data.validation ?? null,
    conflicts: data.conflicts ?? [],
  });

const err = (e: any, fallback: string) => e?.response?.data?.message || fallback;

export const usePrdStore = create<PrdState>((set, get) => ({
  prd: null,
  permissions: null,
  template: null,
  categories: [],
  validation: null,
  conflicts: [],
  loading: false,
  busy: "",

  reset: () =>
    set({ prd: null, permissions: null, template: null, categories: [], validation: null, conflicts: [], busy: "" }),

  fetch: async (projectId) => {
    if (!projectId) return;
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get(`/prd/project/${projectId}`);
      apply(set, data);
    } catch (e: any) {
      // A 403 here is not an error to shout about — it means this person simply
      // does not work with PRDs, and the section stays hidden.
      if (e?.response?.status !== 403) console.error("[prd] fetch failed", e);
      set({ prd: null, permissions: null });
    } finally {
      set({ loading: false });
    }
  },

  generate: async (projectId, opts = {}) => {
    set({ busy: "generate" });
    try {
      const { data } = await axiosInstance.post(`/prd/project/${projectId}/generate`, opts);
      apply(set, data);
      const by = data.prd?.aiReview?.generatedBy;
      toast.success(
        by === "fallback"
          ? "PRD drafted from your project data — the AI service was unavailable, so review it closely."
          : "PRD generated — review it before submitting."
      );
      return null;
    } catch (e: any) {
      // 422 is the template telling us it needs a decision, not a failure.
      if (e?.response?.status === 422) return e.response.data as TemplateState;
      toast.error(err(e, "Couldn't generate the PRD"));
      return null;
    } finally {
      set({ busy: "" });
    }
  },

  save: async (prdId, contentHtml, opts = {}) => {
    set({ busy: "save" });
    try {
      const { data } = await axiosInstance.put(`/prd/${prdId}`, { contentHtml, ...opts });
      apply(set, data);
      toast.success("Draft saved.");
      return null;
    } catch (e: any) {
      if (e?.response?.data?.code === "APPROVED_EDIT") return { needsConfirm: e.response.data.message };
      toast.error(err(e, "Couldn't save the PRD"));
      return null;
    } finally {
      set({ busy: "" });
    }
  },

  regenerate: async (prdId, opts = {}) => {
    set({ busy: "regenerate" });
    try {
      const { data } = await axiosInstance.post(`/prd/${prdId}/regenerate`, opts);
      apply(set, data);
      toast.success(
        data.conflicts?.length
          ? `Regenerated — ${data.conflicts.length} of your edits differ from the new content.`
          : "PRD regenerated."
      );
      return null;
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.code === "CONFIRM_REQUIRED") return { needsConfirm: d.message, editedSections: d.editedSections || [] };
      if (e?.response?.status === 422) { set({ template: d }); toast.error(d?.message || "Template unavailable"); return null; }
      toast.error(err(e, "Couldn't regenerate the PRD"));
      return null;
    } finally {
      set({ busy: "" });
    }
  },

  submit: async (prdId, force = false) => {
    set({ busy: "submit" });
    try {
      const { data } = await axiosInstance.post(`/prd/${prdId}/submit`, { force });
      apply(set, data);
      toast.success("Submitted for approval.");
      return true;
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.code === "INCOMPLETE") {
        set({ validation: d.validation });
        toast.error(`Still empty: ${[...(d.validation?.emptySections || []), ...(d.validation?.missingSections || [])].join(", ")}`);
        return false;
      }
      toast.error(err(e, "Couldn't submit the PRD"));
      return false;
    } finally {
      set({ busy: "" });
    }
  },

  approve: async (prdId, opts = {}) => {
    set({ busy: "approve" });
    try {
      const { data } = await axiosInstance.post(`/prd/${prdId}/approve`, opts);
      apply(set, data);
      if (data.erp?.ok) toast.success("Approved, stored and sent to ERP.");
      else if (data.erp?.state === "Pending — No ERP Project") {
        toast.success("Approved and stored. It will reach ERP as soon as the project does.");
      } else toast.success("Approved and stored. ERP sync is outstanding — you can retry it here.");
      return null;
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.code === "CONFIRM_REQUIRED") return { needsConfirm: d.message, review: d.review };
      // Not a failure to report and walk away from — the author is allowed to
      // decide an empty section is acceptable, so hand it back for them to say so.
      if (d?.code === "INCOMPLETE") { set({ validation: d.validation }); return { incomplete: d.validation }; }
      if (d?.code === "STORAGE_FAILED") { apply(set, d); toast.error(d.message); return null; }
      toast.error(err(e, "Couldn't approve the PRD"));
      return null;
    } finally {
      set({ busy: "" });
    }
  },

  requestChanges: async (prdId, note) => {
    set({ busy: "changes" });
    try {
      const { data } = await axiosInstance.post(`/prd/${prdId}/request-changes`, { note });
      apply(set, data);
      toast.success("Changes requested.");
      return true;
    } catch (e: any) {
      toast.error(err(e, "Couldn't record the review"));
      return false;
    } finally {
      set({ busy: "" });
    }
  },

  retryErp: async (prdId) => {
    set({ busy: "erp" });
    try {
      const { data } = await axiosInstance.post(`/prd/${prdId}/erp-retry`);
      apply(set, data);
      toast[data.erp?.ok ? "success" : "error"](
        data.erp?.ok ? "ERP has the PRD reference." : data.erp?.reason || "ERP did not accept it."
      );
    } catch (e: any) {
      toast.error(err(e, "Couldn't reach ERP"));
    } finally {
      set({ busy: "" });
    }
  },

  republish: async (prdId) => {
    set({ busy: "republish" });
    try {
      const { data } = await axiosInstance.post(`/prd/${prdId}/republish`);
      apply(set, data);
      toast.success("Stored.");
    } catch (e: any) {
      toast.error(err(e, "Couldn't store the document"));
    } finally {
      set({ busy: "" });
    }
  },

  // Fetched as a blob through the authenticated endpoint rather than opened as
  // a URL, so the download carries the session and the storage link is never
  // exposed to the browser.
  download: async (prdId, fileName) => {
    set({ busy: "download" });
    try {
      const res = await axiosInstance.get(`/prd/${prdId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "project-prd.docx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(err(e, "Couldn't download the document"));
    } finally {
      set({ busy: "" });
    }
  },

  unlink: async (prdId) => {
    set({ busy: "unlink" });
    try {
      await axiosInstance.delete(`/prd/${prdId}`);
      get().reset();
      toast.success("PRD removed from this project.");
      return true;
    } catch (e: any) {
      toast.error(err(e, "Couldn't remove the PRD"));
      return false;
    } finally {
      set({ busy: "" });
    }
  },
}));
